import type React from 'react';
import { Bookmark, Palette, Pin, Sparkles, Timer } from 'lucide-react';
import { toast } from 'sonner';
import type { Item } from '@/stores/canvasStore';
import { AnalyzeModal } from '@/components/modals/AnalyzeModal';
import { ScrapedCreativeCard } from '@/components/items/ScrapedCreativeCard';
import { XBookmarksSettings } from '@/components/settings/XBookmarksSettings';
import { followAnalysis, openBoard } from './creative-scout/followAnalysis';
import { FocusPanel } from './focus-timer/FocusPanel';
import { PalettePanel } from './color-palette/PalettePanel';
import { PinterestImportPanel } from './pinterest-import/ImportPanel';
import { followPinterestImport } from './pinterest-import/follow';
import { MANIFESTS } from './manifests';
import type { ExtensionManifest } from './types';

export interface ExtensionPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export interface ExtensionEntry {
  manifest: ExtensionManifest;
  Icon: React.ElementType;
  /** Opened from the launcher. */
  Panel: React.ComponentType<ExtensionPanelProps>;
  /** Card component per item type in manifest.itemTypes. */
  cards: Record<string, React.ComponentType<{ item: Item }>>;
  /**
   * Called with the function's response after a run Partnr started (see
   * runFromPartnr.ts). Owns the success message; without it a generic one shows.
   */
  onRunStarted?: (response: Record<string, unknown>) => void;
}

const UI: Record<string, Omit<ExtensionEntry, 'manifest'>> = {
  'creative-scout': {
    Icon: Sparkles,
    Panel: AnalyzeModal,
    cards: { scraped_creative: ScrapedCreativeCard },
    onRunStarted: (response) => {
      if (response.action === 'analyze_post') {
        toast.success('Pulling the full post. The card updates in a minute or so.');
        return;
      }
      toast.success('Top Creatives started. Results land on a new board in a minute or two.');
      if (typeof response.board_id !== 'string') return;
      followAnalysis(response.board_id, response.extension_job_id as string | undefined, { navigate: false })
        .catch((err) => console.error('Failed to follow Top Creatives run:', err));
    },
  },
  'pinterest-import': {
    Icon: Pin,
    Panel: PinterestImportPanel,
    cards: {},
    onRunStarted: (response) => {
      void followPinterestImport(response);
    },
  },
  'x-bookmarks': {
    Icon: Bookmark,
    Panel: XBookmarksSettings,
    cards: {},
    onRunStarted: (response) => {
      const inserted = Number(response.tweets_inserted ?? 0);
      const boardId = typeof response.board_id === 'string' ? response.board_id : null;
      toast.success(
        inserted > 0 ? `Synced ${inserted} new bookmark${inserted === 1 ? '' : 's'} from X` : 'X bookmarks are up to date',
        boardId && inserted > 0 ? { action: { label: 'Open board', onClick: () => { openBoard(boardId); } } } : undefined,
      );
    },
  },
  'focus-timer': {
    Icon: Timer,
    Panel: FocusPanel,
    cards: {},
  },
  'color-palette': {
    Icon: Palette,
    Panel: PalettePanel,
    cards: {},
  },
};

export const EXTENSIONS: ExtensionEntry[] = MANIFESTS.map((manifest) => {
  const ui = UI[manifest.id];
  if (!ui) throw new Error(`Extension ${manifest.id} has no UI entry in registry.ts`);
  return { manifest, ...ui };
});

export function getExtension(extensionId: string): ExtensionEntry | undefined {
  return EXTENSIONS.find((e) => e.manifest.id === extensionId);
}

const CARDS: Record<string, React.ComponentType<{ item: Item }>> = Object.fromEntries(
  EXTENSIONS.flatMap((e) => Object.entries(e.cards)),
);

/** Card for an extension-owned item type, or undefined for built-in types. */
export function getExtensionCard(itemType: string): React.ComponentType<{ item: Item }> | undefined {
  return CARDS[itemType];
}
